import { ConsentType } from '@imprivata-cloud/authn';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import type { ConsentGrantType } from '../../../src/api/constants';
import { submitConsent } from '../../../src/api/selfServiceServices';
import { useErrorContext } from '../../../src/errorHandler/context/ErrorContext';
import { AppError } from '../../../src/errorHandler/errors';
import { landingPageDataTanstackCacheKey } from '../../../src/portal/containers/users/hooks/useAuthenticators';
import useAgentWindow from '../../hooks/useAgentWindow';
import { SelfServicePath } from '../SelfServiceRoutes';

type FlowState = 'welcome' | 'enrollment' | 'face-enrolled';

interface UseEnrollFaceReturn {
  isModalOpen: boolean;
  screenState: FlowState;
  handleSubmitConsent: (
    userConsentResponse: ConsentGrantType,
    stateOfResidence: string,
    documentNameList: string[],
  ) => void;
  onSuccessfulFaceEnrollment: () => Promise<AppError | undefined>;
  onCancel: () => void;
  onContinue: () => void;
  onEnrollmentDone: () => void;
  onEnrollFlowError: (err: unknown) => void;
}

export const useEnrollFace = (userUpn?: string): UseEnrollFaceReturn => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [screenState, setScreenState] = useState<FlowState>('welcome');

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { emitError } = useErrorContext();
  const { endWorkflowAndCloseWindow } = useAgentWindow();

  const handleSubmitConsent = async (
    userConsentResponse: ConsentGrantType,
    stateOfResidence: string,
    documentNameList: string[],
  ) => {
    try {
      await submitConsent({
        consent: {
          consentType: ConsentType.BIOMETRICS,
          residenceLocationShortName: stateOfResidence,
          grantType: userConsentResponse,
          documentNameList,
          userId: '',
        },
        userUpn,
      });

      queryClient.invalidateQueries({ queryKey: [landingPageDataTanstackCacheKey], exact: true });

      if (userConsentResponse === 'not-granted') {
        setIsModalOpen(false);
        navigate(SelfServicePath.SELF_SERVICE_LANDING_PAGE);
      } else {
        setScreenState('enrollment');
        setIsModalOpen(true);
      }
    } catch (err: unknown) {
      if (err instanceof AppError) {
        emitError(err);
      } else {
        emitError(AppError.fromMessage('Error while submitting consent'));
      }
    }
  };

  const onCancel = () => {
    setIsModalOpen(false);
    endWorkflowAndCloseWindow();
    navigate(SelfServicePath.SELF_SERVICE_LANDING_PAGE);
  };

  const onEnrollmentDone = () => {
    endWorkflowAndCloseWindow();
    navigate(`${SelfServicePath.SELF_SERVICE_LANDING_PAGE}`);
  };

  const onContinue = () => {
    setScreenState('enrollment');
    setIsModalOpen(true);
  };

  const onSuccessfulFaceEnrollment = useCallback(async () => {
    setIsModalOpen(false);
    queryClient.invalidateQueries({ queryKey: [landingPageDataTanstackCacheKey], exact: true });
    setScreenState('face-enrolled');

    // Close agent window after 2 seconds on successful enrollment
    setTimeout(endWorkflowAndCloseWindow, 2000);
    return undefined;
  }, [queryClient.invalidateQueries, endWorkflowAndCloseWindow]);

  const onEnrollFlowError = useCallback((error: unknown) => {
    console.log('Error at enrolling: ', (error as AppError).message);
  }, []);

  return {
    isModalOpen,
    screenState,
    handleSubmitConsent,
    onSuccessfulFaceEnrollment,
    onCancel,
    onContinue,
    onEnrollmentDone,
    onEnrollFlowError,
  };
};
